import { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Title,
  Group,
  Button,
  Table,
  TextInput,
  Modal,
  Text,
  ActionIcon,
  Badge,
  ThemeIcon,
  Loader,
  Center,
  Alert,
  Card,
} from "@mantine/core";
import { IconPlus, IconTrash, IconCategory, IconAlertCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { api } from "@/lib/api";

export default function ImageTypeList() {
  const [types, setTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.imageType.list();
      setTypes(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    if (!name.trim() || !label.trim()) return;
    setSaving(true);
    try {
      await api.imageType.create({ name: name.trim().toLowerCase(), label: label.trim() });
      notifications.show({
        title: "Амжилттай",
        message: `"${label}" нэмэгдлээ`,
        color: "green",
      });
      setName("");
      setLabel("");
      setModalOpen(false);
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, typeName: string) => {
    try {
      await api.imageType.remove(id);
      notifications.show({
        title: "Амжилттай",
        message: `"${typeName}" устгагдлаа`,
        color: "green",
      });
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={3}>Зургийн төрөл</Title>
          <Badge variant="light" size="lg">
            {types.length}
          </Badge>
        </Group>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setModalOpen(true)}
          size="sm"
        >
          Төрөл нэмэх
        </Button>
      </Group>

      <Alert variant="light" color="blue" icon={<IconAlertCircle size={16} />}>
        <Text size="sm">
          Зургийн төрөл нь тухайн төсөлд хамаарна. Зураг хуулахдаа сонгож оруулна.
        </Text>
      </Alert>

      {loading ? (
        <Center h={300}>
          <Loader />
        </Center>
      ) : types.length === 0 ? (
        <Center h={300}>
          <Stack align="center" gap="xs">
            <ThemeIcon size={48} variant="light" color="gray" radius="xl">
              <IconCategory size={24} />
            </ThemeIcon>
            <Text size="sm" c="dimmed">
              Зургийн төрөл бүртгээгүй байна
            </Text>
            <Button variant="light" size="xs" onClick={() => setModalOpen(true)}>
              Төрөл нэмэх
            </Button>
          </Stack>
        </Center>
      ) : (
        <Card shadow="xs" padding={0} radius="md" withBorder>
          <Table.ScrollContainer minWidth={400}>
            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr style={{ backgroundColor: "var(--app-table-header)" }}>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>
                    #
                  </Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>
                    Нэр (key)
                  </Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>
                    Харагдах нэр
                  </Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={60}>
                    Устгах
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {types.map((t, idx) => (
                  <Table.Tr key={t._id}>
                    <Table.Td c="dimmed" fz="sm">
                      {idx + 1}
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="md">
                        {t.name}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{t.label}</Text>
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        size="sm"
                        variant="light"
                        radius="md"
                        color="red"
                        onClick={() => handleDelete(t._id, t.label)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={<Text fw={600}>Зургийн төрөл нэмэх</Text>}
        size="sm"
        centered
      >
        <Stack>
          <TextInput
            label="Нэр (key)"
            placeholder="Жишээ: plate, car"
            value={name}
            onChange={(e) => { const v = e.currentTarget.value; setName(v); }}
            size="sm"
            description="Англи жижиг үсгээр, хоосон зайгүй"
          />
          <TextInput
            label="Харагдах нэр"
            placeholder="Жишээ: Улсын дугаар, Машин"
            value={label}
            onChange={(e) => { const v = e.currentTarget.value; setLabel(v); }}
            size="sm"
          />
          <Button onClick={handleAdd} loading={saving} size="sm" disabled={!name.trim() || !label.trim()}>
            Нэмэх
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
